import ContractVersionContents, {
  type PopupData,
} from "~/features/contracts/components/AddToCartPopup/ContractVersionContents";

import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ContractDetailsResponse } from "~/features/contracts/models/models";
import { Permission, userHasAnyPermission } from "~/domains/user/utils/permissions";
import { fakeDescriptorFromProduct } from "~/domains/product/utils/productDescriptorUtils";
import { CART_PAGE } from "~/utils/url/staticUrls";
import { type ProductData } from "~/domains/product/types/ProductData";
import { Button, Stack } from "~/common/mui";
import { ContractAddToCartPopupStates } from "~/features/contracts/components/AddToCartPopup/ContractAddToCartPopupStates";
import { Alert } from "~/common/mui/Alert/Alert";
import { ProductDescriptorContext } from "~/domains/product/ProductContext";
import { AtcContentLayout } from "~/features/addToCart/components/AddToCartProcess/shell/AtcContentLayout";
import { AtcDialogShell } from "~/features/addToCart/components/AddToCartProcess/shell/AtcDialogShell";
import { AtcProductDetails } from "~/features/addToCart/components/AddToCartProcess/shell/AtcProductDetails";
import { LoadingScreen } from "~/features/addToCart/components/AddToCartProcess/Modals/LoadingScreen";
import { useSelector } from "react-redux";
import type { RootState } from "~/app/store";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

interface AddToCartPopupProps {
  product: ProductData;
  contractDetails: ContractDetailsResponse;
  contractStatus: ContractAddToCartPopupStates;
  onClose: () => void;
  onAdd: (data: PopupData) => void;
}

const AddContractToCartDialog = ({ product, contractDetails, contractStatus, onClose, onAdd }: AddToCartPopupProps) => {
  const { t } = useTranslation();

  const productDescriptor = fakeDescriptorFromProduct(product);
  const packageSize = contractDetails.packSize;
  const packageCode = contractDetails.packCode;

  const methods = useForm({
    defaultValues: { selectedPack: { packSize: packageSize }, selectedPackCode: packageCode },
  });

  const selectedDate = useSelector((state: RootState) => state.contracts.popupData.selectedDate);
  const selectedQty = useSelector((state: RootState) => state.contracts.popupData.selectedQty);

  let content;
  switch (contractStatus) {
    case ContractAddToCartPopupStates.LOADING: {
      content = <LoadingScreen />;
      break;
    }
    case ContractAddToCartPopupStates.SENDING: {
      content = <LoadingScreen />;
      break;
    }
    case ContractAddToCartPopupStates.LOADED: {
      content = (
        <FormProvider {...methods}>
          <form>
            <ContractVersionContents
              packageSize={packageSize}
              productDescriptor={productDescriptor}
              contractDetails={contractDetails}
              packCode={packageCode}
            />
          </form>
        </FormProvider>
      );
      break;
    }
    case ContractAddToCartPopupStates.SEND_SUCCESS: {
      content = <Alert severity={"info"}>{t(`addToCartPopup.add.success`)}</Alert>;
      break;
    }
    case ContractAddToCartPopupStates.ERROR_LOADING: {
      content = <Alert severity={"info"}>{t(`hybrisAjaxError`)}</Alert>;
      break;
    }
    case ContractAddToCartPopupStates.SEND_FAILURE: {
      content = <Alert severity={"error"}>{t(`addToCartPopup.add.error`)}</Alert>;
      break;
    }
    default:
      content = undefined;
  }

  const showAddToCart = userHasAnyPermission(Permission.ADD_TO_CART, Permission.ADD_TO_QUOTE);

  let primaryButton;
  if (!showAddToCart) {
    primaryButton = null;
  } else if (contractStatus === ContractAddToCartPopupStates.LOADED) {
    const quantity = selectedQty || packageSize;
    const requestedDeliveryDate = selectedDate;
    const okLabel = t(`addToCartPopup.add.submitBtn`);
    const isPackageSizeInvalid = !quantity || !Number.isInteger(quantity / packageSize);
    const isDateInvalid = !requestedDeliveryDate;
    const addToCartDisabled = window.SCHA.config.disableAllWS || isPackageSizeInvalid || isDateInvalid;
    const sendData = () => {
      const quantity = selectedQty || packageSize;
      if (quantity !== undefined && selectedDate !== undefined) {
        onAdd({
          quantity,
          requestedDeliveryDate: selectedDate,
        });
      }
    };
    primaryButton = (
      <Button onClick={sendData} startIcon={<ShoppingCartIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />} disabled={addToCartDisabled}>
        {okLabel}
      </Button>
    );
  } else {
    const okDisabled = window.SCHA.config.disableAllWS;
    primaryButton = (
      <Button href={CART_PAGE} startIcon={<ShoppingCartIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />} disabled={okDisabled}>
        {t("addToCartPopup.checkoutBtn")}
      </Button>
    );
  }

  const cancelLabel =
    contractStatus === ContractAddToCartPopupStates.SEND_SUCCESS ? t("addToCartPopup.backToStore") : t("cancel");

  const footer = (
    <Stack direction={"row"} gap={1}>
      <Button data-test-id={"add-to-cart-btn-cancel"} variant={"text"} onClick={onClose}>
        {cancelLabel}
      </Button>
      {primaryButton}
    </Stack>
  );

  return (
    <ProductDescriptorContext value={productDescriptor}>
      <AtcDialogShell title={"Add contract to cart"} onClose={onClose} footer={footer}>
        <AtcContentLayout
          info={<AtcProductDetails descriptor={productDescriptor} packCode={packageCode} />}
          main={content}
        />
      </AtcDialogShell>
    </ProductDescriptorContext>
  );
};

export default AddContractToCartDialog;
